/**
 * Parse <think>...</think> reasoning tags from model responses.
 * Used by Qwen3 and other reasoning models that wrap chain-of-thought
 * in <think> blocks.
 */

export interface ParsedContent {
  /** Extracted reasoning content (null if no <think> block) */
  thinking: string | null
  /** Main response content (everything outside <think>) */
  content: string
}

/**
 * Parse completed (non-streaming) content.
 * Extracts <think>...</think> block and returns thinking + clean content separately.
 */
export function parseThinking(raw: string): ParsedContent {
  const match = raw.match(/<think>([\s\S]*?)<\/think>/)
  if (!match) return { thinking: null, content: raw.trim() }

  const thinking = match[1].trim()
  const content = raw.replace(/<think>[\s\S]*?<\/think>/, '').trim()

  return { thinking: thinking || null, content }
}

/**
 * Check if streaming content is currently inside a <think> block.
 * Returns { isThinking, thinkingContent, visibleContent }.
 */
export function parseStreamingThinking(accumulated: string): {
  isThinking: boolean
  thinkingContent: string
  visibleContent: string
} {
  // Completed <think>...</think> — extract and show main content
  const closedMatch = accumulated.match(/^<think>([\s\S]*?)<\/think>([\s\S]*)$/)
  if (closedMatch) {
    return {
      isThinking: false,
      thinkingContent: closedMatch[1].trim(),
      visibleContent: closedMatch[2].trim(),
    }
  }

  // Still inside <think> — no </think> yet
  const openMatch = accumulated.match(/^<think>([\s\S]*)$/)
  if (openMatch) {
    return {
      isThinking: true,
      thinkingContent: openMatch[1].trim(),
      visibleContent: '',
    }
  }

  // No <think> at all — normal content
  return {
    isThinking: false,
    thinkingContent: '',
    visibleContent: accumulated,
  }
}
